import type {Price} from '@/redux/v1/models';

import {defaultSearchParamsByTravelType} from '@/config';
import {PAGE_HOTELS} from '@/consts';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useLocalSplit} from '@/modules/common/hooks/useLocalSplit';
import {useSearchParams} from '@/modules/common/hooks/useSearchParams';
import {useAppSelector} from '@/redux/config';
import {getCrossBordersData as selectHotelsCrossBorderData} from '@/redux/v1/hotels/list/reducer';
import {getCrossBordersData as selectOffersCrossBorderData} from '@/redux/v1/offers/list/reducer';

type Treatment =
  | 'off'
  | 'on_with_price'
  | 'on_without_price'
  | 'ibe3_only_with_price'
  | 'ibe3_only_without_price'
  | 'v1_with_price'
  | 'v1_without_price'
  | 'v2_without_price';

type Config = {
  version: 1 | 2;
  url: string;
  airports: Array<string>;
  priceTotal: Price;
  priceEnabled: boolean;
};

type CrossBorderBannerSplit = {
  ready: boolean;
  enabled: boolean;
  treatment: string;
  config: null | Config;
};

export const useCrossBorderBannerSplit = (): CrossBorderBannerSplit => {
  const currentPage = useCurrentPage();
  const [searchParams] = useSearchParams();
  const hotelsData = useAppSelector(selectHotelsCrossBorderData);
  const offersData = useAppSelector(selectOffersCrossBorderData);
  const split = useLocalSplit<Treatment>('CAP_324_cross_borders_sales');

  const crossBordersData =
    currentPage === PAGE_HOTELS ? hotelsData : offersData;
  const defaultAirports = defaultSearchParamsByTravelType.packageTour.airports;
  // Workaround to hide Cross Borders Sales banner in case all airports have been selected
  const areAllAirportsSelected =
    searchParams.airports?.length === defaultAirports.length &&
    searchParams.airports.every((code) => defaultAirports.includes(code));

  const data =
    !areAllAirportsSelected && crossBordersData ? crossBordersData : null;
  const enabled =
    data !== null &&
    (split.treatment === 'v1_with_price' ||
      split.treatment === 'v1_without_price' ||
      split.treatment === 'v2_without_price');
  const version =
    split.treatment === 'v1_with_price' ||
    split.treatment === 'v1_without_price'
      ? 1
      : 2;

  return {
    ready: split.ready,
    enabled,
    treatment: split.treatment,
    config: data
      ? {
          version,
          url: data.url,
          airports: data.airports,
          priceTotal: data.priceTotal,
          priceEnabled: split.treatment === 'v1_with_price',
        }
      : null,
  };
};
